import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import { FaSave, FaEye, FaArrowLeft, FaCog, FaHandshake } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../utils/roles';
import './BookingSettings.css';

interface OnlineBookingSettings {
    title: string;
    description: string;
    fields: {
        customerName: { label: string; placeholder: string };
        phone: { label: string; placeholder: string };
        email: { label: string; placeholder: string };
        service: { label: string; placeholder: string };
        date: { label: string; placeholder: string };
        time: { label: string; placeholder: string };
        staff: { label: string; placeholder: string };
        referrer: { label: string; placeholder: string; visible: boolean };
        notes: { label: string; placeholder: string };
    };
    footerText: string;
}

const BookingSettings: React.FC = () => {
    const { user } = useAuth();
    const [settings, setSettings] = useState<OnlineBookingSettings | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    const canEdit = user?.role === ROLES.OWNER || user?.role === ROLES.MANAGER || user?.role === ROLES.ADMIN;

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await apiService.getOnlineBookingSettings();
            if (response.data) {
                setSettings(response.data);
            }
            setLoading(false);
        } catch (err) {
            console.error('Error fetching settings:', err);
            setMessage({ type: 'error', text: 'Failed to load settings.' });
            setLoading(false);
        }
    };

    const handleFieldChange = (fieldKey: string, property: 'label' | 'placeholder', value: string) => {
        if (!settings) return;
        setSettings({
            ...settings,
            fields: {
                ...settings.fields,
                [fieldKey]: {
                    ...(settings.fields as any)[fieldKey],
                    [property]: value
                }
            }
        });
    };

    const handleReferrerToggle = () => {
        if (!settings) return;
        setSettings({
            ...settings,
            fields: {
                ...settings.fields,
                referrer: {
                    ...settings.fields.referrer,
                    visible: !settings.fields.referrer.visible
                }
            }
        });
    };

    const handleGeneralChange = (key: keyof OnlineBookingSettings, value: string) => {
        if (!settings) return;
        setSettings({
            ...settings,
            [key]: value
        });
    };

    const handleSave = async () => {
        if (!settings) return;
        setSaving(true);
        setMessage(null);
        try {
            await apiService.updateOnlineBookingSettings(settings);
            setMessage({ type: 'success', text: 'Settings saved successfully!' });
            setTimeout(() => setMessage(null), 3000);
        } catch (err) {
            setMessage({ type: 'error', text: 'Failed to save settings.' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="settings-loading">Loading settings...</div>;
    if (!settings) return <div className="settings-error">Could not load settings.</div>;

    if (!canEdit) {
        return (
            <div className="booking-settings-container">
                <div className="settings-error-card">
                    <h2>⚠️ Access Denied</h2>
                    <p>You do not have permission to manage online booking settings. Only Owner, Manager, or Admin roles can access this page.</p>
                    <button className="btn-save" onClick={() => window.history.back()}>Go Back</button>
                </div>
            </div>
        );
    }

    return (
        <div className="booking-settings-container">
            <div className="settings-header">
                <div className="header-title">
                    <FaCog className="title-icon" />
                    <h1>Online Booking Settings</h1>
                </div>
                <div className="header-actions">
                    <button className="btn-preview" onClick={() => window.open('/book', '_blank')}>
                        <FaEye /> Preview Page
                    </button>
                    <button className="btn-save" onClick={handleSave} disabled={saving}>
                        <FaSave /> {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </div>

            {message && (
                <div className={`message-banner ${message.type}`}>
                    {message.text}
                </div>
            )}

            <div className="settings-grid">
                <section className="settings-card">
                    <h2><FaCog /> General Appearance</h2>
                    <div className="form-group">
                        <label>Page Title</label>
                        <input
                            type="text"
                            value={settings.title}
                            onChange={(e) => handleGeneralChange('title', e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>Welcome Message (Description)</label>
                        <textarea
                            value={settings.description}
                            onChange={(e) => handleGeneralChange('description', e.target.value)}
                            rows={3}
                        />
                    </div>
                    <div className="form-group">
                        <label>Footer Text</label>
                        <input
                            type="text"
                            value={settings.footerText}
                            onChange={(e) => handleGeneralChange('footerText', e.target.value)}
                        />
                    </div>
                </section>

                <section className="settings-card">
                    <h2><FaHandshake /> Referrer Field</h2>
                    <div className="toggle-group">
                        <label className="switch">
                            <input
                                type="checkbox"
                                checked={settings.fields.referrer.visible}
                                onChange={handleReferrerToggle}
                            />
                            <span className="slider round"></span>
                        </label>
                        <span>Enable "Referrer" field on booking page</span>
                    </div>
                    {settings.fields.referrer.visible && (
                        <div className="field-edit-row">
                            <div className="form-group">
                                <label>Field Label</label>
                                <input
                                    type="text"
                                    value={settings.fields.referrer.label}
                                    onChange={(e) => handleFieldChange('referrer', 'label', e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label>Placeholder</label>
                                <input
                                    type="text"
                                    value={settings.fields.referrer.placeholder}
                                    onChange={(e) => handleFieldChange('referrer', 'placeholder', e.target.value)}
                                />
                            </div>
                        </div>
                    )}
                </section>

                <section className="settings-card full-width">
                    <h2>Form Field Customization</h2>
                    <p className="section-hint">Customize labels and placeholders for standard fields.</p>

                    <div className="fields-customizer">
                        {Object.entries(settings.fields).map(([key, field]) => {
                            if (key === 'referrer') return null;
                            const f = field as { label: string, placeholder: string };
                            return (
                                <div key={key} className="field-config-item">
                                    <div className="field-name">{key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}</div>
                                    <div className="field-inputs">
                                        <div className="form-group">
                                            <label>Label</label>
                                            <input
                                                type="text"
                                                value={f.label}
                                                onChange={(e) => handleFieldChange(key, 'label', e.target.value)}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Placeholder</label>
                                            <input
                                                type="text"
                                                value={f.placeholder}
                                                onChange={(e) => handleFieldChange(key, 'placeholder', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default BookingSettings;
